
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useApiRequestMutation } from "../../services/apiService";
import ToastrService from "../../services/toastService";
import "./ChangePassword.css";
import useAdminLogout from "../Layout/useAdminLogout";

const ChangePasswordModal = ({
    isOpen,
    onClose,
    disconnectSocket,
    disconnectCall,
    changePasswordUrl = "/admin/changePassword",
    logoutUrl = "/admin/logout",
}) => {
    const navigate = useNavigate();
    const [apiRequest] = useApiRequestMutation();

    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const { Logout } = useAdminLogout();

    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [touched, setTouched] = useState({});

    useEffect(() => {
        if (isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
        });

        setShowCurrent(false);
        setShowNew(false);
        setShowConfirm(false);
        setTouched({});
    };

    const validatePassword = (password) => {
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
            password
        );
    };

    const getPasswordStrength = (password) => {
        if (!password) {
            return { score: 0, label: "" };
        }

        let score = 0;

        if (password.length >= 8) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[a-z]/.test(password)) score++;
        if (/\d/.test(password)) score++;
        if (/[@$!%*?&]/.test(password)) score++;

        if (score <= 2) return { score, label: "Weak" };
        if (score <= 4) return { score, label: "Medium" };

        return { score, label: "Strong" };
    };

    const passwordStrength = getPasswordStrength(
        passwordData.newPassword
    );

    const handleChange = (event) => {
        const { name, value } = event.target;

        setPasswordData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    const handleBlur = (event) => {
        setTouched((previous) => ({
            ...previous,
            [event.target.name]: true,
        }));
    };

    const getError = (field) => {
        if (!touched[field]) return "";

        if (!passwordData[field]) {
            const messages = {
                currentPassword: "Current password is required",
                newPassword: "New Password is required",
                confirmPassword: "Confirm password is required",
            };

            return messages[field];
        }

        if (
            field === "newPassword" &&
            !validatePassword(passwordData.newPassword)
        ) {
            return "Password must be at least 8 characters and include uppercase, lowercase, number, and special character";
        }

        if (
            field === "confirmPassword" &&
            passwordData.confirmPassword !== passwordData.newPassword
        ) {
            return "Passwords do not match";
        }

        return "";
    };

    const isFormValid =
        Boolean(passwordData.currentPassword) &&
        Boolean(passwordData.newPassword) &&
        Boolean(passwordData.confirmPassword) &&
        validatePassword(passwordData.newPassword) &&
        passwordData.newPassword === passwordData.confirmPassword;

    const handleClose = () => {
        if (isLoading || isLoggingOut) return;

        resetForm();
        onClose?.();
    };

    const togglePasswordVisibility = (field) => {
        const setters = {
            currentPassword: setShowCurrent,
            newPassword: setShowNew,
            confirmPassword: setShowConfirm,
        };

        setters[field]((previous) => !previous);
    };

    const handleVisibilityKeyDown = (event, field) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            togglePasswordVisibility(field);
        }
    };

    const renderPasswordIcon = (field, isVisible) => (
        <span
            className="password-icon"
            role="button"
            tabIndex={isLoading || isLoggingOut ? -1 : 0}
            aria-label={
                isVisible
                    ? `Hide ${field === "currentPassword"
                        ? "current password"
                        : field === "newPassword"
                            ? "new password"
                            : "confirm password"}`
                    : `Show ${field === "currentPassword"
                        ? "current password"
                        : field === "newPassword"
                            ? "new password"
                            : "confirm password"}`
            }
            aria-pressed={isVisible}
            onClick={() => {
                if (!isLoading && !isLoggingOut) {
                    togglePasswordVisibility(field);
                }
            }}
            onKeyDown={(event) =>
                handleVisibilityKeyDown(event, field)
            }
        >
            {isVisible ? <FaEye /> : <FaEyeSlash />}
        </span>
    );

    const logoutAndNavigate = async (destination) => {
        if (isLoggingOut) return;

        setIsLoggingOut(true);

        try {
            disconnectSocket?.();
            disconnectCall?.();

            const response = await apiRequest({
                url: logoutUrl,
                method: "POST",
                data: {},
            }).unwrap();

            if (response?.success) {

                const theme = localStorage.getItem("theme");

                localStorage.clear();

                if (theme !== null) {
                    localStorage.setItem("theme", theme);
                }
                resetForm();
                onClose?.();
                navigate(destination, { replace: true });
            } else {
                ToastrService.error(
                    response?.message ||
                    "Unable to logout. Please try again."
                );
            }
        } catch (error) {
            ToastrService.error(
                error?.data?.message ||
                error?.error?.message ||
                error?.message ||
                "Unable to logout. Please try again."
            );
        } finally {
            setIsLoggingOut(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        setTouched({
            currentPassword: true,
            newPassword: true,
            confirmPassword: true,
        });

        if (!isFormValid || isLoading || isLoggingOut) return;

        setIsLoading(true);

        try {
            const response = await apiRequest({
                url: changePasswordUrl,
                method: "POST",
                data: {
                    oldPassword: passwordData.currentPassword,
                    newPassword: passwordData.newPassword,
                },
            }).unwrap();

            if (response?.success) {
                ToastrService.success(
                    response?.message ||
                    "Password changed successfully. Please login again to continue!"
                );

                resetForm();
                onClose?.();

                await logoutAndNavigate("/");
            } else {
                ToastrService.error(
                    response?.message ||
                    "Something went wrong. Please try again later."
                );
            }
        } catch (error) {
            ToastrService.error(
                error?.data?.message ||
                error?.error?.message ||
                error?.message ||
                "Something went wrong. Please try again later."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (isLoading || isLoggingOut) return;

        resetForm();
        onClose?.();

        await logoutAndNavigate("/forgotPassword");
    };

    if (!isOpen) return null;

    const fieldsDisabled = isLoading || isLoggingOut;

    return (
        <>
            <div
                className="modal fade show change-password-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="changePasswordTitle"
                style={{ display: "block" }}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content border-0 shadow rounded-3 p-4">

                        {/* Header */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6
                                id="changePasswordTitle"
                                className="fw-semibold text-primary mb-0"
                            >
                                Change Password
                            </h6>

                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                onClick={handleClose}
                                disabled={fieldsDisabled}
                            />
                        </div>

                        {/* Form */}
                        <div className="change-password-form">
                            <form onSubmit={handleSubmit} noValidate>

                                {/* Current Password */}
                                <div className="form-group mb-4">
                                    <label
                                        htmlFor="currentPassword"
                                        className="form-label"
                                    >
                                        Current Password{" "}
                                        <span className="text-danger">*</span>
                                    </label>

                                    <div className="password-field">
                                        <input
                                            id="currentPassword"
                                            type={
                                                showCurrent
                                                    ? "text"
                                                    : "password"
                                            }
                                            className="form-control"
                                            name="currentPassword"
                                            placeholder="Enter current password"
                                            value={passwordData.currentPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={fieldsDisabled}
                                            autoComplete="current-password"
                                        />

                                        {renderPasswordIcon(
                                            "currentPassword",
                                            showCurrent
                                        )}
                                    </div>

                                    {getError("currentPassword") && (
                                        <p className="change-password-error">
                                            {getError("currentPassword")}
                                        </p>
                                    )}
                                </div>

                                {/* New Password */}
                                <div className="form-group mb-4 password-wrapper">
                                    <label
                                        htmlFor="newPassword"
                                        className="form-label"
                                    >
                                        New Password{" "}
                                        <span className="text-danger">*</span>
                                    </label>

                                    <div className="password-field">
                                        <input
                                            id="newPassword"
                                            type={
                                                showNew ? "text" : "password"
                                            }
                                            className="form-control"
                                            name="newPassword"
                                            placeholder="Enter new password"
                                            value={passwordData.newPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={fieldsDisabled}
                                            autoComplete="new-password"
                                        />

                                        {renderPasswordIcon(
                                            "newPassword",
                                            showNew
                                        )}
                                    </div>

                                    {getError("newPassword") && (
                                        <p className="change-password-error">
                                            {getError("newPassword")}
                                        </p>
                                    )}

                                    {passwordData.newPassword && (
                                        <>
                                            <div className="password-strength-text text-dark mt-2">
                                                Strength:{" "}
                                                <span
                                                    className={`strength-text ${passwordStrength.label.toLowerCase()}`}
                                                >
                                                    {passwordStrength.label}
                                                </span>
                                            </div>

                                            <div
                                                className="strength-bar"
                                                role="progressbar"
                                                aria-valuenow={
                                                    passwordStrength.score
                                                }
                                                aria-valuemin={0}
                                                aria-valuemax={5}
                                                aria-label="Password strength"
                                            >
                                                <div
                                                    className={`strength-fill ${passwordStrength.label.toLowerCase()}-bg`}
                                                    style={{
                                                        width: `${(passwordStrength.score / 5) *
                                                            100
                                                            }%`,
                                                    }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>

                                {/* Confirm Password */}
                                <div className="form-group mb-4 password-wrapper">
                                    <label
                                        htmlFor="confirmPassword"
                                        className="form-label"
                                    >
                                        Confirm Password{" "}
                                        <span className="text-danger">*</span>
                                    </label>

                                    <div className="password-field">
                                        <input
                                            id="confirmPassword"
                                            type={
                                                showConfirm
                                                    ? "text"
                                                    : "password"
                                            }
                                            className="form-control"
                                            name="confirmPassword"
                                            placeholder="Confirm new password"
                                            value={passwordData.confirmPassword}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            disabled={fieldsDisabled}
                                            autoComplete="new-password"
                                        />

                                        {renderPasswordIcon(
                                            "confirmPassword",
                                            showConfirm
                                        )}
                                    </div>

                                    {getError("confirmPassword") && (
                                        <p className="change-password-error">
                                            {getError("confirmPassword")}
                                        </p>
                                    )}
                                </div>

                                {/* Footer */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        type="button"
                                        className="btn btn-link text-primary small p-0 text-decoration-none"
                                        onClick={handleForgotPassword}
                                        disabled={fieldsDisabled}
                                    >
                                        Forgot Password?
                                    </button>

                                    <button
                                        type="submit"
                                        className="btn btn-primary py-2 btn-submit"
                                        disabled={
                                            fieldsDisabled || !isFormValid
                                        }
                                    >
                                        {isLoading ? (
                                            <span
                                                className="spinner-border spinner-border-sm"
                                                role="status"
                                                aria-label="Changing password"
                                            />
                                        ) : (
                                            "Submit"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal-backdrop fade show change-password-backdrop" />
        </>
    );
};

export default ChangePasswordModal;
